import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { keys as incidentTacticKeys } from './incidentTacticHooks';
import { TacticBase } from '../../../types/iapBaseTypes';

export type Tactic = TacticBase & {
  id: UUID;
  group_guid: UUID;
  archived: boolean;
  strategies: any;
};

export const keys = {
  base: 'tactics',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useTactics = (isEnabled: boolean) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.get(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<Tactic[]>(
        `${config.apiGateway.ics_tactics}/${group_guid}`
      );
      return resp.data;
    },
    enabled: isEnabled,
  });
};

export type CreateTacticReqData = TacticBase & {
  incident_id?: UUID;
  incident_resources?: UUID[];
  strategies?: UUID[];
};

export const useCreateTactic = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTacticReqData) => {
      return await axios.post(`${config.apiGateway.ics_tactics}`, {
        ...data,
        group_guid,
        user_guid,
      });
    },
    onSuccess: (_, reqObj) => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
      if (reqObj.incident_id) {
        queryClient.invalidateQueries({
          queryKey: incidentTacticKeys.all,
        });
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export type EditTacticReqData = TacticBase & {
  id: UUID;
  archived: boolean;
};

export const useEditTactic = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditTacticReqData) => {
      const { id, ...updateData } = data;
      return await axios.put(`${config.apiGateway.ics_tactics}/${id}`, {
        ...updateData,
        group_guid,
        user_guid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
